/**
 * UI foundation 1.0c — numbers, money, hours, dates and months in the
 * reader's language.
 *
 * - Locale follows the i18n language: et → et-EE, en → en-GB, ru → ru-RU,
 *   fi → fi-FI (anything else → et-EE, the default locale).
 * - The time zone is ALWAYS an argument (the order's / sheet's / site's IANA
 *   zone). The device zone is never used: an invalid or missing zone means
 *   the legacy site clock, Europe/Tallinn (`workTimeZone`, PB-04).
 * - Instant → wall clock goes through shared-types `calendarDate` /
 *   `wallTime`, the same arithmetic the server and `siteTime.ts` use; this
 *   module only lays the result out in the locale. A date-only value
 *   (`2026-10-25`) and a wall-clock value (`2026-10-25T03:30`) are already
 *   calendar values and are not shifted.
 */
import { calendarDate, wallTime, workTimeZone } from '@rendimees24/shared-types';
import type { TFunction } from 'i18next';

export type IntlLocale = 'et-EE' | 'en-GB' | 'ru-RU' | 'fi-FI';

const LOCALES: Record<string, IntlLocale> = {
  et: 'et-EE',
  en: 'en-GB',
  ru: 'ru-RU',
  fi: 'fi-FI',
};

/** The Intl locale for an i18n language code (`en`, `en-US`, `ru` …). */
export function intlLocale(language: string | undefined | null): IntlLocale {
  return LOCALES[(language ?? '').toLowerCase().split(/[-_]/)[0] ?? ''] ?? 'et-EE';
}

/** What a missing or unreadable value shows (same as `siteTime.ts`). */
export const MISSING = '—';

export type VatMode = 'incl' | 'excl';

export interface Formatters {
  readonly locale: IntlLocale;
  /** `145,16 €`; with `vat` → `145,16 € + KM` / `145,16 € koos KM-ga` (i18n keys). */
  money(eur: number | null | undefined, options?: { vat?: VatMode }): string;
  /** A plain number in the locale (decimal comma vs point, grouping). */
  number(value: number | null | undefined, options?: Intl.NumberFormatOptions): string;
  /** Hours with at most two decimals: `7,5 h` (i18n key). */
  hours(value: number | null | undefined): string;
  /** The calendar date in `zone`. */
  date(iso: string | null | undefined, zone: string | null | undefined): string;
  /** The wall-clock time (24 h) in `zone`. */
  time(iso: string | null | undefined, zone: string | null | undefined): string;
  /** Date and time in `zone`. */
  dateTime(iso: string | null | undefined, zone: string | null | undefined): string;
  /** A work month `YYYY-MM` → `oktoober 2026`. */
  month(month: string | null | undefined): string;
}

const finite = (value: number | null | undefined): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const OFFSET = /(?:Z|[+-]\d{2}:?\d{2})$/i;
const WALL = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/;

/**
 * The wall-clock parts of a value in `zone`, carried as a UTC Date so Intl
 * can lay it out with `timeZone: 'UTC'` and no further shifting.
 */
function wallDate(
  value: string | null | undefined,
  zone: string | null | undefined,
  withTime: boolean,
): Date | null {
  if (!value || !Number.isFinite(Date.parse(value))) return null;
  const tz = workTimeZone(zone);
  const wall = OFFSET.test(value)
    ? withTime
      ? wallTime(value, tz)
      : calendarDate(value, tz)
    : value;
  const m = WALL.exec(wall);
  if (!m) return null;
  if (withTime && m[4] === undefined) return null;
  return new Date(
    Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4] ?? 0), Number(m[5] ?? 0)),
  );
}

const DATE: Intl.DateTimeFormatOptions = {
  timeZone: 'UTC',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
};
const TIME: Intl.DateTimeFormatOptions = {
  timeZone: 'UTC',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
};

/** Pure: the formatters for one i18n language. `useFormatters()` binds it to the live language. */
export function createFormatters(t: TFunction, language: string | undefined | null): Formatters {
  const locale = intlLocale(language);
  const eur = new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' });
  const hoursFormat = new Intl.NumberFormat(locale, { maximumFractionDigits: 2 });
  const dateFormat = new Intl.DateTimeFormat(locale, DATE);
  const timeFormat = new Intl.DateTimeFormat(locale, TIME);
  const dateTimeFormat = new Intl.DateTimeFormat(locale, { ...DATE, ...TIME });
  const monthFormat = new Intl.DateTimeFormat(locale, {
    timeZone: 'UTC',
    month: 'long',
    year: 'numeric',
  });

  return {
    locale,
    money(value, options) {
      if (!finite(value)) return MISSING;
      const amount = eur.format(value);
      if (options?.vat === 'excl')
        return t('common:format.money.vatExcl', { amount, defaultValue: '{{amount}} + KM' });
      if (options?.vat === 'incl')
        return t('common:format.money.vatIncl', { amount, defaultValue: '{{amount}} koos KM-ga' });
      return amount;
    },
    number(value, options) {
      if (!finite(value)) return MISSING;
      return new Intl.NumberFormat(locale, options).format(value);
    },
    hours(value) {
      if (!finite(value)) return MISSING;
      return t('common:format.hours', {
        value: hoursFormat.format(value),
        defaultValue: '{{value}} h',
      });
    },
    date(iso, zone) {
      const d = wallDate(iso, zone, false);
      return d ? dateFormat.format(d) : MISSING;
    },
    time(iso, zone) {
      const d = wallDate(iso, zone, true);
      return d ? timeFormat.format(d) : MISSING;
    },
    dateTime(iso, zone) {
      const d = wallDate(iso, zone, true);
      return d ? dateTimeFormat.format(d) : MISSING;
    },
    month(month) {
      const m = /^(\d{4})-(\d{2})$/.exec(month ?? '');
      if (!m || Number(m[2]) < 1 || Number(m[2]) > 12) return MISSING;
      return monthFormat.format(new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 1)));
    },
  };
}
